/**
 * How-to content — Front office (reception, reservations, folios, gate)
 * and Food & Beverage (waiter, kitchen, cashier, restaurant manager, chef).
 * Format: see guides.exec.js.
 */

export const FRONT_GUIDES = {
  /* ═════════════════════════ RECEPTION ═════════════════════════ */
  main_reception: {
    title: 'Reception',
    intro: 'Check guests in and out, book conferences and transport, and keep an eye on rooms — all from here.',
    faq: [
      { q: 'The room I want is not in the list', a: 'Only Available rooms show. Check Room Status: it may be Occupied, Dirty, Inspecting or in Maintenance.' },
      { q: 'The guest paid by M-Pesa but has no code', a: 'Ask for the M-Pesa SMS; the code is the 10-character reference at the start (e.g. SJK3ABC123).' },
      { q: 'Invoice did not print', a: 'Allow pop-ups for m-hotels.co.ke in your browser, then use 🖨 Invoice to reprint.' },
    ],
    sections: [
      { h: 'Check in a walk-in guest', d: 'Register a guest and give them a room.',
        steps: [
          'Open the **Check-In** tab.',
          { t: 'Type the **Full Name**, **ID / Passport** and **Phone** (all required). Add **Email** if they have one.', tip: 'Check the ID physically. Use +254… format for Kenyan phones so SMS offers reach them.' },
          { t: 'Choose the **Room**.', tip: 'Only rooms that are free right now are listed. If none fit, look at Room Status.' },
          'Set the **Check-In** and **Check-Out** dates, and the number of **Adults** and **Children**.',
          { t: 'Choose the **Payment Mode**: Cash, M-Pesa, Card / PDQ, Invoice or Complimentary.', tip: 'Invoice = company pays later. Complimentary needs a manager’s approval.' },
          'Click **✓ Check In Guest**.',
        ],
        result: 'A message confirms the check-in, the room turns Occupied and the guest appears under In-House.',
        mistakes: ['Checking a guest into a room marked Dirty.', 'Typing the phone without the country code.'],
      },
      { h: 'Check out a guest', d: 'Settle the bill and free the room.',
        steps: [
          'Open **In-House** and click **Check Out** next to the guest.',
          { t: 'Review the bill: **Accommodation**, **Outstanding Charges** (restaurant, bar, laundry…) and the **GRAND TOTAL**.', tip: 'If something looks wrong, check the guest’s folio before taking payment.' },
          { t: 'Choose how they pay: Cash, M-Pesa, Card or Bank Transfer, and type the **Transaction Code**.', tip: 'For M-Pesa you can click 📱 M-Pesa STK Push — the guest gets a payment prompt on their phone and just enters their PIN.' },
          'Confirm the checkout.',
        ],
        result: 'The invoice prints, the room is flagged for housekeeping, and the guest gets a CRM profile with loyalty points (10 per night + 1 per KES 1,000 spent).',
        mistakes: ['Checking out before all restaurant/bar charges are posted — they would be missed.'],
      },
      { h: 'Reprint an invoice', d: 'Give the guest a copy of their bill at any time.',
        steps: ['Open **In-House**.', 'Click **🖨 Invoice** next to the guest.'],
      },
      { h: 'Book a conference', d: 'Reserve a hall for a meeting or event.',
        steps: [
          'Open **Conference** and click **+ New Conference Booking**.',
          'Choose the **Venue**, type the **Event Name** (e.g. Annual AGM 2025) and **Event Date**.',
          { t: 'Choose the **Session Type**: Full Day, Half Day AM or Half Day PM, and the **Number of Attendees**.', tip: 'Available Venues shows each hall’s half-day and full-day rates.' },
          'Type the **Organiser Name**, phone, organisation and email.',
          'Tick any **Additional Services** (projector, mic, catering) and add special requests.',
          'Check the **Booking Summary** and click **✓ Confirm Conference Booking**.',
          'Later, open the booking with **View** to **💰 Record Payment** and **🖨 Print Invoice**.',
        ],
        result: 'A confirmation code is shown and the booking is saved.',
      },
      { h: 'Book guest transport', d: 'Airport transfers, excursions and town runs.',
        steps: [
          'Open **Transport** and click **+ Book Transport**.',
          'Type the **Guest Name** and **Phone**.',
          { t: 'Type the **Pickup Location** (e.g. Hotel Reception) and pick the **Destination** from **Quick select zone** — or type a custom one.', tip: 'Zones fill in the standard price automatically.' },
          'Set **Pickup Date & Time** and **Passengers**.',
          'Enter the **Fee (KES)**, **Payment Mode** and **Transaction Code**.',
          { t: 'Add **Notes / Special Instructions** (flight number, luggage) and save.', tip: 'Filter bookings with Pending, Active, Today or All Time. Cancel Booking if the guest cancels.' },
        ],
        result: 'The driver sees the trip on their screen straight away.',
      },
      { h: 'Use the dashboard and room status', d: 'Your overview of the hotel.',
        steps: [
          'Open **Dashboard** for occupancy, check-outs due, revenue today, RevPAR and guest satisfaction.',
          'Use **⚡ Quick Actions** to jump to New Reservation, Check-In, Check-Out, Generate Invoice, Book Conference or Report Maintenance.',
          { t: 'Open **Room Status** to see every room’s occupancy and cleaning state.', tip: 'Only give out rooms that are clean. “✓ All rooms clean” means housekeeping is up to date.' },
        ],
      },
      { h: 'Pool, kids and donations at the desk', d: 'Other desk tasks.',
        steps: [
          'Open **Pool & Kids** to record pool entries and kids activities (see the Pool & Kids guide for details).',
          'Open **Donations** to record a donation received at the desk.',
        ],
      },
    ],
  },

  /* ═════════════════════════ RESERVATIONS ═════════════════════════ */
  reservations: {
    title: 'Reservations',
    intro: 'Bookings made on your website arrive here. Confirm them, assign rooms and check guests in on arrival.',
    faq: [{ q: 'I cannot find a booking', a: 'Check the All tab and search by phone or reference code; clear the date filters.' }],
    sections: [
      { h: 'Confirm a new online booking', d: 'Turn a website request into a confirmed stay.',
        steps: [
          'Open the **Pending** tab and click **View** on the booking.',
          'Check the **Guest Details**, **Est. Total**, **Deposit** and **Special Requests**.',
          { t: 'Check the deposit. The banner shows the deposit due and the balance due at check-in.', tip: 'If no deposit arrives within 48 hours the booking may be cancelled.' },
          'Optionally choose a room in **Assign Room (optional)**.',
          'Click **✓ Confirm Reservation**, then **🖨 Print Confirmation** if the guest wants a copy.',
        ],
        result: 'The booking moves to Confirmed.',
      },
      { h: 'Check in a guest with a booking', d: 'On arrival day.',
        steps: [
          'Search the guest by name, phone, email or reference code.',
          'Open the booking and click **🏨 Check In Now**.',
        ],
        result: 'The guest becomes In-House and the room turns Occupied.',
      },
      { h: 'Find any booking', d: 'Search and filter.',
        steps: [
          'Use the tabs **Pending**, **Confirmed**, **Checked-In**, **Cancelled** or **All**.',
          'Type in **🔍 Search** (name, phone, email, ref code…).',
          'Filter by **Room Type** and **Check-In From** / **Check-Out To**. **Clear** removes filters.',
        ],
      },
      { h: 'Handle transport requests and cancellations', d: 'Extra requests on bookings.',
        steps: [
          { t: 'Bookings marked **🚐 Transport Requested** need a trip booked.', tip: 'Book it from Reception → Transport and confirm the fee with the guest.' },
          'If the guest cancels, open the booking and click **✕ Cancel**.',
        ],
      },
    ],
  },

  /* ═════════════════════════ FOLIOS ═════════════════════════ */
  folio: {
    title: 'Guest Folios',
    intro: 'A folio is a guest’s running bill during their stay. Everything they charge to the room collects here and is paid at checkout.',
    faq: [{ q: 'A restaurant charge is missing from the folio', a: 'The waiter must choose Charge to Room and link the guest. Otherwise post it manually with + Post Charge.' }],
    sections: [
      { h: 'Open a guest’s folio', d: 'See everything a guest owes.',
        steps: [
          'Type the guest’s name in **🔍 Search guest…** and select them.',
          'Read **Room Charges**, **Incidentals** and **Total Bill**.',
          'Use the tabs **All Charges**, **Outstanding** and **Summary by Type**.',
        ],
      },
      { h: 'Post a charge to a room', d: 'Add laundry, minibar, spa or any service to the bill.',
        steps: [
          'With the guest selected, click **+ Post Charge**.',
          'Choose the **Charge Type**: Food & Restaurant, Bar & Beverages, Swimming Pool, Laundry, Spa & Wellness, Mini Bar, Telephone, Conference or Other.',
          { t: 'Type a clear **Description** (e.g. Laundry — 3 shirts, 2 trousers).', tip: 'The guest will read this on their bill, so be specific.' },
          'Type the **Amount (KES)** and **Quantity**. Check **Total to post**.',
          'Click **Post Charge**.',
        ],
        result: 'The charge appears under Outstanding with ● Pending.',
        mistakes: ['Posting a restaurant bill again after the waiter already charged it to the room — check All Charges first.'],
      },
      { h: 'Settle charges and print the bill', d: 'Take payment for extras or give the guest a statement.',
        steps: [
          '**🖨 Print Folio** prints the guest’s statement at any time.',
          { t: 'To take payment now, choose **Settle Incidentals**, pick the method (💵 Cash, 📱 M-Pesa, 🏦 Bank Transfer, 💳 PDQ / Card) and type the **Transaction / Reference Code**.', tip: 'Choose 🏨 Add to Room Bill instead to pay everything at checkout.' },
          'Settled charges show **✓ Settled**. “All charges settled — folio is clear” means nothing is owed.',
          'Click **🚪 Check Out** to close the folio and check the guest out from here.',
        ],
      },
    ],
  },

  /* ═════════════════════════ GATE ═════════════════════════ */
  main_gate: {
    title: 'Gate Control',
    intro: 'Log every visitor and vehicle in and out. The screen refreshes itself every 15 seconds.',
    faq: [
      { q: 'The visitor pass will not print', a: 'A vehicle number plate is required for a pass. For visitors on foot, record them without a pass.' },
      { q: 'The camera scanner does not start', a: 'Allow camera access in the browser, or type the 32-character code from the pass and press Enter.' },
    ],
    sections: [
      { h: 'Register a visitor coming in', d: 'Record who enters, and issue a pass.',
        steps: [
          'Open **Register Entry**.',
          'Choose the **Visitor Type** (guest, supplier, contractor, visitor…).',
          { t: 'Type the **Full Name** and **ID / Passport** — both are required.', tip: 'Look at the actual ID card; do not just ask.' },
          'Type the **Phone**.',
          { t: 'Type the **Vehicle Number Plate** (e.g. KAA 000A).', tip: 'A plate is needed to print a pass. For people on foot leave it empty — they are recorded without a pass.' },
          'Type the **Purpose of Visit** (e.g. Meeting, Delivery), choose the **Department** and type the **Host Person** being visited.',
          'Add **Notes** if anything is unusual, then save.',
          'Click **🖨 Print Visitor Pass** and hand it to the visitor.',
        ],
        result: 'The visitor appears under Inside with their time in.',
      },
      { h: 'Record a visitor leaving', d: 'Close their visit.',
        steps: [
          'Open **QR Exit Scan**.',
          { t: 'Click **📱 Scan with Phone / Webcam Camera** and hold the pass QR code in front of the camera.', tip: 'A USB handheld scanner works too — it submits on its own.' },
          'No camera? Type or paste the 32-character code from the pass and press Enter.',
          'Check the **Scan Result**, then **← Scan Another** for the next visitor.',
          { t: 'Or open **Inside** and click **Exit →** next to the visitor.' },
        ],
        result: 'The visitor moves from Inside to Exited with their exit time.',
      },
      { h: 'Check who is inside', d: 'Especially before closing at night.',
        steps: [
          'Open **Inside** — everyone on the premises now, with time in.',
          '**Today** lists all of today’s entries.',
          'Before closing, make sure Inside is empty or everyone left is accounted for.',
        ],
      },
      { h: 'Search the log and export', d: 'For investigations and reports.',
        steps: [
          'Open **Full Log** and search by name, ID, plate or phone; filter by dates, type and status.',
          { t: 'Open **Export**, set **Date From** / **Date To** and filters, then **Download Excel** or **Download PDF**.', tip: 'Excel has the full table plus a summary sheet; PDF is a formatted report ready to print.' },
          'Open **Visitor Analytics**, choose a period and Top 10/20/50 to see unique, repeat and frequent visitors.',
        ],
      },
    ],
  },

  /* ═════════════════════════ WAITER ═════════════════════════ */
  main_waiter: {
    title: 'Waiter',
    intro: 'Take orders, send them to the kitchen, follow them, and collect payment.',
    faq: [
      { q: 'The order will not submit', a: 'Enter a Table / Seat and choose a Kitchen Station — both are required.' },
      { q: 'Payment stays “Awaiting cashier confirmation”', a: 'The cashier has not confirmed it yet. Tell the cashier; they confirm it in the Payment Queue.' },
    ],
    sections: [
      { h: 'Take an order', d: 'Send a customer’s order to the kitchen.',
        steps: [
          'Open **New Order**.',
          { t: 'In the **Menu**, tap **＋ Add** on each item. Adjust quantities in **Order Details**.', tip: 'Use the category filters to find items faster.' },
          'Type the **Table / Seat** (required).',
          { t: 'Choose the **Kitchen Station** (required) — it decides which kitchen prepares it.', tip: '“✓ Will be sent to: Main Kitchen” confirms where it goes.' },
          { t: 'For a hotel guest, choose them in **Link to Hotel Guest**, then pick the **Billing Mode**: 🏨 Charge to Room or 💵 Pay Separately.', tip: 'Charge to Room posts the bill to their folio; they pay at checkout.' },
          'Add a **Kitchen Note** for allergies or requests (e.g. no onions).',
          'Submit the order.',
        ],
        result: 'The order appears on the kitchen screen immediately.',
        mistakes: ['Choosing the wrong kitchen station — the order goes to a kitchen that will not see it.'],
      },
      { h: 'Follow the order and serve', d: 'Know when food is ready.',
        steps: [
          { t: 'Open **Kitchen** to track your orders: Pending → Preparing → Ready! → Served.', tip: 'A banner “READY TO COLLECT & SERVE” and a sound tell you when food is ready.' },
          'Collect it from the pass and serve.',
        ],
      },
      { h: 'Collect payment', d: 'Get the bill paid and confirmed.',
        steps: [
          'Open **My Bills** and click **💳 Request Customer Payment** on the bill.',
          { t: 'In **Select Payment Method** choose Cash, M-Pesa, Bank Transfer, PDQ / Card, Room Charge, Complimentary or Stripe.' },
          { t: 'For anything except cash, type the **transaction code**.', tip: 'M-Pesa code = 10 characters at the start of the customer’s SMS (e.g. SJK3ABC123).' },
          'Tap **Request Payment**. A payment slip prints automatically.',
          'The bill shows **⏳ Awaiting cashier confirmation…** until the cashier checks the money.',
        ],
        result: 'When the cashier confirms, it shows ✓ Paid · Receipt issued by cashier.',
        mistakes: ['Typing a made-up code — the cashier will reject it.'],
      },
      { h: 'See your history', d: 'Your orders and sales.',
        steps: ['Open **My History** for all your orders and bills.', 'The tiles at the top show today’s orders, active kitchen orders and pending bills.'],
      },
    ],
  },

  /* ═════════════════════════ KITCHEN ═════════════════════════ */
  main_kitchen: {
    title: 'Kitchen',
    intro: 'The kitchen display shows new orders for your station. It refreshes itself every 8 seconds.',
    faq: [{ q: 'I see no orders', a: 'Check your selected station matches the station waiters choose. Orders for other stations are hidden.' }],
    sections: [
      { h: 'Choose your station', d: 'Only see orders for your kitchen.',
        steps: [
          'In **📍 Select Your Kitchen Station**, tap your station.',
          { t: 'Not listed? Type it in **Or type a custom station** and click **Set**.', tip: 'Use exactly the same name waiters choose (e.g. Main Kitchen).' },
        ],
      },
      { h: 'Prepare an order', d: 'Move each order through the kitchen.',
        steps: [
          { t: 'When you start cooking, tap **▶ Start Preparing**.', tip: 'This starts the timer used in the Kitchen Prep Report.' },
          'When the food is plated, tap **✓ Mark Ready**. The waiter is alerted.',
          'When it leaves the pass, tap **✓ Served**.',
          'Always work oldest orders first.',
        ],
        result: 'Waiters know exactly when to collect food.',
        mistakes: ['Forgetting ▶ Start Preparing — the order is not timed and looks late in reports.'],
      },
    ],
  },

  /* ═════════════════════════ CASHIER ═════════════════════════ */
  main_cashier: {
    title: 'Cashier',
    intro: 'You confirm every payment. Nothing counts as revenue until you confirm it. The screen refreshes every 10 seconds.',
    faq: [
      { q: 'The code the waiter entered is wrong', a: 'Use Cashier Override to enter the correct mode and code before confirming.' },
      { q: 'Receipt did not print', a: 'Allow pop-ups for m-hotels.co.ke, then reprint from Completed.' },
    ],
    sections: [
      { h: 'Confirm a payment', d: 'Check the money and issue the receipt.',
        steps: [
          'Open **Payment Queue**. Each bill shows the items, **TOTAL**, payment mode and code the waiter submitted.',
          { t: 'Check the money is really there: count cash, or find the M-Pesa / card code on your statement or PDQ slip.', tip: 'Match the amount AND the code. Never confirm on the customer’s word alone.' },
          { t: 'If the waiter chose the wrong method or code, use **Cashier Override** to set the correct **Override Payment Mode** and **Override Tx / Reference Code**.', tip: '**Clear Override** undoes it.' },
          'Click **✓ Confirm & Mark as Paid**. The official receipt prints.',
          'If the money did not arrive, reject the payment and tell the waiter.',
        ],
        result: 'The bill is paid, the waiter sees ✓ Paid, and revenue updates everywhere.',
        mistakes: ['Confirming before the M-Pesa message arrives.'],
      },
      { h: 'Look up bills and reprint receipts', d: 'Find any bill.',
        steps: ['**All Bills** shows every bill and its status.', '**Completed** shows bills you confirmed — reprint receipts from here.'],
      },
      { h: 'Raise a complimentary bill', d: 'For free meals or services approved by a manager.',
        steps: [
          'Open **Complimentary** and start a **New Complimentary Bill**.',
          'Optionally choose **Attach to Existing Bill**.',
          'Type the **Guest Name**, choose the **Category** (VIP Guest, Complaint Resolution, Staff Meal, Marketing / Promotion, Billing Error, Other) and type the **Amount (KES)**.',
          { t: 'Write a clear **Reason / Justification**.', tip: 'Managers reject vague reasons. Example: “Cold food served to table 4, manager offered free dessert.”' },
          'Submit.',
        ],
        result: 'It waits for manager approval, then is expensed — not counted as a sale.',
      },
      { h: 'Sell pool entry', d: 'Day passes at the cashier desk.',
        steps: [
          'Open **Pool**. For a hotel guest choose them in **Hotel Guest (optional)**; otherwise type the **Visitor Name**.',
          'Choose the **Rate** (Adult day pass, Child day pass, Hotel guest rate) and number of **Persons**.',
          'Choose the **Payment Mode** and type the code.',
          'Click **🏊 Process Pool Entry**. **Pool Entries Today** lists them.',
        ],
      },
      { h: 'Test M-Pesa and card payments', d: 'Practise before live payments are switched on.',
        steps: [
          { t: 'Under **⚡ Test Payment**, use the **M-Pesa STK Push Simulation** with test number 0712345678.', tip: 'The code fills in automatically; then click Confirm.' },
          'Or use the **Stripe Payment Simulation** with the test card buttons (✓ Visa Success, ✗ Card Declined…).',
          'Live M-Pesa and card payments work once the administrator adds the real keys on the server.',
        ],
      },
    ],
  },

  /* ═════════════════════════ RESTAURANT MANAGER ═════════════════════════ */
  main_restaurant_manager: {
    title: 'Restaurant Manager',
    intro: 'Oversee sales, menus and staff; approve voids, discounts, refunds, returns, shopping lists and complimentaries. Choose the period at the top.',
    faq: [
      { q: 'I cannot delete a category', a: 'Move or delete the menu items that use it first.' },
      { q: 'Why is a reason required to void?', a: 'Voids, discounts and refunds are audited to prevent losses. The reason is saved in the Audit Trail.' },
    ],
    sections: [
      { h: 'Check performance', d: 'How the restaurant and bar are doing.',
        steps: [
          'Choose the **Period** at the top.',
          'Open **Dashboard**: F&B, food and bar revenue, total orders, average order, voided orders, gross margin, daily trend, peak hours, payment modes, top items and F&B health.',
          'Open **Analytics** for item performance (top 20), payment methods and category sales.',
          'Open **Staff Performance** for revenue and orders by waiter.',
        ],
      },
      { h: 'Void an order', d: 'Cancel an order entered by mistake.',
        steps: [
          'Open **Orders** and click **Void** on the order.',
          { t: 'Type the **Void Reason** (required) and click **⚠ Confirm Void**.', tip: 'The reason is recorded in the audit trail.' },
        ],
        result: 'The order shows VOIDED and is excluded from revenue.',
      },
      { h: 'Discount, refund or reopen a bill', d: 'Fix a bill after a complaint or mistake.',
        steps: [
          'Open **Bills** and find the bill.',
          { t: '**Disc**: type the **Discount % (1–100)** and a reason, then **Apply Discount**.' },
          { t: '**Refund**: type the **Refund Amount (KES)** and a reason, then **Process Refund**.' },
          '**Reopen**: reopens a closed bill so items can be corrected.',
        ],
      },
      { h: 'Manage the menu and prices', d: 'Add, price and organise dishes and drinks.',
        steps: [
          { t: 'Open **Menu & Pricing** and click **+ Category** to create a category (e.g. Grills, Cocktails). Set the **Sort Order** — lower appears first.' },
          'Click **+ Add Item**: name, Food or Bar, category, **Selling Price (KES)**, **Cost Price (KES)** and description.',
          { t: 'Untick **Available for ordering** to hide an item temporarily (e.g. out of stock).', tip: 'Entering the cost price shows the margin for each item.' },
          'Use **Edit** or **Delete**. A category in use cannot be deleted.',
        ],
      },
      { h: 'Handle a customer return', d: 'Wrong, damaged or unwanted items.',
        steps: [
          'Open **Returns** → **+ Log New Return**.',
          'Type the item, choose the **Source** (Restaurant, Bar, Gift Shop, Room Service) and **Reason** (wrong item, defective, quality complaint, overcharged, not ordered, other).',
          'Enter **Quantity**, **Unit Price (KES)**, customer name and **Description — What happened?**.',
          'Click **Submit Return Note**. Pending returns are approved with **✓ Approve** or rejected with a reason.',
        ],
      },
      { h: 'Approve shopping lists and complimentaries', d: 'Approvals waiting for you.',
        steps: [
          { t: 'Open **Shopping Lists**. Review items and estimated total, then **✓ Approve** or **✕ Reject** (reason required).', tip: 'Approved lists go to the accountant to release funds.' },
          'Open **Complimentaries**. Approve (it is expensed) or reject; **🖨️ Print Authorization** prints it with the stamp.',
        ],
      },
      { h: 'Price a new dish', d: 'Work out the right selling price.',
        steps: [
          'Open **Food Costing**. Type the dish name, number of portions and overhead/labour %.',
          'Add each ingredient with quantity and cost.',
          'Set the desired profit margin, rounding and taxes (VAT 16%, Catering Levy 2%, Service Charge 10%).',
          { t: 'Read the **Suggested Selling Price** and **Food Cost %**.', tip: 'Aim for food cost around 28–35%.' },
          'Click **📋 Publish to Waiter Menu** to add it to the food or bar menu.',
        ],
      },
      { h: 'Close the day', d: 'End-of-day summary.',
        steps: ['Click **🖨 Print Daily Summary**.', 'Check **Activity Log** (last 200 events) for anything unusual.'],
      },
    ],
  },

  /* ═════════════════════════ CHEF ═════════════════════════ */
  main_chef: {
    title: 'Chef — Food Costing',
    intro: 'Know what every dish costs, price it right, control waste and keep the kitchen within budget.',
    faq: [
      { q: 'Cost per portion looks wrong', a: 'Check the ingredient purchase price, unit and yield %. A low yield % raises the real cost.' },
      { q: 'My spoilage report is locked', a: 'Approved or rejected reports cannot be edited. Create a new report if needed.' },
    ],
    sections: [
      { h: 'Add an ingredient', d: 'Every recipe is built from ingredients.',
        steps: [
          'Open **Ingredients** and click **+ Add Ingredient**.',
          'Type the **Ingredient Name**, choose the **Purchase Unit** (kg, litre, piece…) and type the **Purchase Price (KES)**.',
          { t: 'Type the **Yield % (1–100)** — the usable part after trimming.', tip: 'Example: a whole chicken may be 70% usable meat. The EP Cost Preview shows the real cost of the usable part.' },
          'Choose the category, supplier, **Stock Qty** and **Reorder Level**, then save.',
        ],
        result: '⚠ LOW STOCK appears when stock falls to the reorder level. 📈 Price History tracks price changes.',
      },
      { h: 'Cost a recipe', d: 'What each portion really costs.',
        steps: [
          'Open **Recipes** and click **+ New Recipe**.',
          'Type the **Dish Name**, **Portions (yield)** and **Target Food Cost %** (e.g. 30).',
          'Click **+ Add Ingredient** and add each ingredient with its quantity.',
          'Read **Cost Per Portion** in the Cost Summary and type your **Actual Selling Price (KES)**.',
          { t: 'Check the status: ✅ Under Target, ⚠️ At Risk or 🔴 Over Target.', tip: 'Over target? Raise the price, reduce portion size or find a cheaper supplier.' },
        ],
        result: 'The recipe shows cost, price and profit per portion.',
      },
      { h: 'Scale a recipe or print it', d: 'Cook for a bigger group.',
        steps: [
          'Click **⚖️ Scale** on a recipe, type the new number of portions and **Calculate Scale**.',
          'Click **📄 PDF** to download a recipe card for the kitchen.',
        ],
      },
      { h: 'Find the right selling price', d: 'Price calculator for new dishes.',
        steps: [
          'Open **Price Calculator**. Type the dish, portions and overhead %, and add ingredients.',
          'Set the desired profit margin, rounding and taxes.',
          'Read the **Suggested Selling Price**.',
          'Click **📋 Publish to Waiter Menu** to put it on the menu straight away.',
        ],
      },
      { h: 'Decide what to keep on the menu', d: 'Menu engineering.',
        steps: [
          'Open **Menu Engineering**. Dishes are grouped by popularity and profit.',
          'Promote popular, profitable dishes; reprice or rework the rest; consider dropping dishes that are neither.',
          'Use the **Menu Engineering Strategy Guide** on screen.',
        ],
      },
      { h: 'Report spoilage', d: 'Record food that was wasted so it is costed properly.',
        steps: [
          'Open **Spoilage** and click **+ New Spoilage Report**.',
          'Type a title (e.g. Morning Kitchen Waste — Fridge Failure) and the date.',
          'Choose the category (expired, contaminated, overcooked, customer return, damaged, unsold leftover, other) and describe what happened and how to prevent it.',
          { t: 'Under **📦 Spoiled Items**, add each item: name, quantity, unit and unit value. **+ Add Another Item** for more.', tip: 'Link items to ingredients so stock is reduced when approved.' },
          'Save, then click **📤 Submit for Approval**.',
        ],
        result: 'The accountant approves it (cost posted to Food & Beverage expenses, ingredient stock reduced) or rejects it with a reason.',
        mistakes: ['Not reporting waste — the kitchen then looks like it is losing stock.'],
      },
      { h: 'Manage the budget and staff meals', d: 'Stay within the monthly kitchen budget.',
        steps: [
          'Open **Budget & Meals** and click **+ Set Budget** for the month: ingredients, staff meals and overhead/gas.',
          'Compare **Total Budget** with **Total Actual Spend** during the month.',
          { t: 'Each day click **+ Log Meal**: date, meal type (Breakfast, Lunch, Dinner, Tea Break), menu, **Number of Staff** and **Cost per Head (KES)**.', tip: 'Staff meals are recorded as a kitchen expense.' },
        ],
      },
    ],
  },
};
